import { useCallback, useMemo, useState } from "react";
import { OrderedMap } from "immutable";
import { useQueryParam, StringParam } from "routing/QueryParams";
import usePaginationQueryParameter, { DEFAULT_PAGE } from "hooks/usePaginationQueryParameter";
import useUrlQueryFilters from "components/common/EntityFilters/hooks/useUrlQueryFilters";
export const useWithURLParams = (layoutConfig) => {
  const [urlQueryFilters, setUrlQueryFilters] = useUrlQueryFilters();
  const [query, setUrlQuery] = useQueryParam("query", StringParam);
  const urlPagination = usePaginationQueryParameter(void 0, layoutConfig.pageSize, false);
  const fetchOptions = useMemo(
    () => ({
      query,
      page: urlPagination.page,
      pageSize: layoutConfig.pageSize,
      sort: layoutConfig.sort,
      filters: urlQueryFilters
    }),
    [layoutConfig.pageSize, layoutConfig.sort, urlPagination.page, query, urlQueryFilters]
  );
  const onChangeFilters = useCallback(
    (newUrlQueryFilters) => {
      urlPagination.resetPage();
      setUrlQueryFilters(newUrlQueryFilters);
    },
    [urlPagination, setUrlQueryFilters]
  );
  return {
    fetchOptions,
    setQuery: setUrlQuery,
    onChangeFilters,
    paginationState: urlPagination
  };
};
export const useWithLocalState = (layoutConfig) => {
  const [transientFetchOptions, setTransientFetchOptions] = useState({
    query: "",
    page: DEFAULT_PAGE,
    filters: OrderedMap()
  });
  const setPagination = useCallback(
    ({ page, pageSize }) => {
      setTransientFetchOptions({
        ...transientFetchOptions,
        page,
        pageSize
      });
    },
    [transientFetchOptions]
  );
  const resetPage = useCallback(() => {
    setTransientFetchOptions({
      ...transientFetchOptions,
      page: DEFAULT_PAGE,
      sort: layoutConfig.sort
    });
  }, [transientFetchOptions, layoutConfig.sort]);
  const onChangeFilters = useCallback(
    (newFilters) => {
      setTransientFetchOptions({
        ...transientFetchOptions,
        page: DEFAULT_PAGE,
        filters: newFilters
      });
    },
    [transientFetchOptions]
  );
  const setQuery = useCallback(
    (newQuery) => {
      setTransientFetchOptions({
        ...transientFetchOptions,
        query: newQuery,
        page: DEFAULT_PAGE
      });
    },
    [transientFetchOptions]
  );
  const fetchOptions = useMemo(
    () => ({
      ...transientFetchOptions,
      pageSize: layoutConfig.pageSize,
      sort: layoutConfig.sort
    }),
    [transientFetchOptions, layoutConfig.pageSize, layoutConfig.sort]
  );
  return useMemo(
    () => ({
      fetchOptions,
      setQuery,
      onChangeFilters,
      paginationState: {
        page: fetchOptions.page,
        pageSize: fetchOptions.pageSize,
        resetPage,
        setPagination
      }
    }),
    [fetchOptions, onChangeFilters, resetPage, setPagination, setQuery]
  );
};
